import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import MasterViewController from "../../redux/actions/masterApiActions";
import DesignationFormModal from "./AddDesignation";
import ExportButton from "../../components/ag grid/ExportButton";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const DesignationList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const { hasPermission } = usePermission();

  const [showModal, setShowModal] = useState(false);
  const [editDesignation, setEditDesignation] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const { getDesignations, deleteDesignation } = MasterViewController();
  const { designations } = useSelector((state) => state.master);

  const canCreate = hasPermission("designations", "create");
  const canEdit = hasPermission("designations", "edit");
  const canDelete = hasPermission("designations", "delete");

  useEffect(() => {
    dispatch(getDesignations());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteDesignation(id)),
    [dispatch, deleteDesignation]
  );

  const handleEdit = useCallback((designation) => {
    setEditDesignation(designation);
    setShowModal(true);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Designation Name",
        field: "name",
        flex: 1,
        minWidth: 200,
      },
      ...(canEdit || canDelete
        ? [
            {
              headerName: "Actions",
              field: "actions",
              pinned: "right",
              width: 120,
              minWidth: 120,
              maxWidth: 120,
              suppressSizeToFit: true,
              sortable: false,
              cellRenderer: (params) => (
                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(params.data)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDelete(params.data.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit, canDelete, handleEdit, handleDelete]
  );

  return (
    <ListPageLayout
      title="Designations"
      subtitle="Manage job titles and designations for your workforce"
      headerAction={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditDesignation(null);
              setShowModal(true);
            }}
            sx={toolbarButtonSx}
          >
            Add Designation
          </Button>
        ) : null
      }
      toolbarRight={
        <>
          <ExportButton
            gridRef={gridRef}
            fileName="designations.xlsx"
            exportColumns={["name"]}
            disabled={selectedRows.length === 0}
          />
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          rowData={designations}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No designations yet"
          emptyHint={
            canCreate
              ? 'Click "Add Designation" to create your first designation.'
              : "No designation records are available."
          }
        />
      </div>

      {showModal && (
        <DesignationFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editDesignation}
        />
      )}
    </ListPageLayout>
  );
};

export default DesignationList;
